// src/context/CsrfContext.tsx - CSRF Token Management
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { API_URL } from '../services/apiClient';

interface CsrfContextValue {
  csrfToken: string | null;
  refreshCsrfToken: () => Promise<void>;
  loading: boolean;
}

const CsrfContext = createContext<CsrfContextValue | undefined>(undefined);

interface CsrfProviderProps {
  children: ReactNode;
}

export const CsrfProvider: React.FC<CsrfProviderProps> = ({ children }) => {
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchCsrfToken = async () => {
    try {
      const response = await fetch(`${API_URL}/csrf-token`, {
        credentials: 'include' // Important: include cookies
      });
      
      if (response.ok) {
        const data = await response.json();
        setCsrfToken(data.csrfToken);
      } else {
        console.error('Failed to fetch CSRF token:', response.statusText);
      }
    } catch (error) {
      console.error('Error fetching CSRF token:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshCsrfToken = async () => {
    setLoading(true);
    await fetchCsrfToken();
  };

  useEffect(() => {
    fetchCsrfToken();
    
    // Refresh CSRF token every 30 minutes
    const interval = setInterval(fetchCsrfToken, 30 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <CsrfContext.Provider value={{ csrfToken, refreshCsrfToken, loading }}>
      {children}
    </CsrfContext.Provider>
  );
};

export const useCsrf = (): CsrfContextValue => {
  const context = useContext(CsrfContext);
  if (context === undefined) {
    throw new Error('useCsrf must be used within a CsrfProvider');
  }
  return context;
};
